import React, { useMemo, useState } from 'react';

import { Alert, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import MarketingRouterRuleTableRow, {
  MARKETING_ROUTER_RULE_TABLE_COLUMNS,
  MarketingRouterRuleTableHead,
} from '~/pages/Marketing/MarketingRouterRuleTableRow';

import { useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import { getRulesApplyingChannelMarkup } from './channelMarkupConnectedRules';

const CONNECTED_ROUTERS_PAGE_SIZE = 100;

type Props = {
  channelMarkupId: string;
};

/**
 * Marketing router tests with a variant that applies this channel markup, in
 * any state: scheduled, running or ended.
 */
export default function ChannelMarkupConnectedRoutersSection({ channelMarkupId }: Props) {
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    channelMarkupId,
    page: 0,
    pageSize: CONNECTED_ROUTERS_PAGE_SIZE,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = useMemo(
    () => getRulesApplyingChannelMarkup(rulesQuery.data ?? [], channelMarkupId),
    [rulesQuery.data, channelMarkupId],
  );
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  // Rule dates are Sydney calendar days, so "today" has to be one too.
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);

  return (
    <Stack
      spacing={1.5}
      component="section"
      aria-labelledby="channel-markup-connected-routers-title"
      sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
    >
      <Stack spacing={0.5}>
        <Typography id="channel-markup-connected-routers-title" variant="h6">
          Marketing router tests{rulesQuery.isSuccess ? ` (${rules.length})` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Marketing router rules with a variant that applies this channel markup.
        </Typography>
      </Stack>
      {rulesQuery.isError ? (
        <Alert severity="error">
          {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router tests.'}
        </Alert>
      ) : null}
      <Table size="small" aria-labelledby="channel-markup-connected-routers-title">
        <MarketingRouterRuleTableHead />
        <TableBody>
          {rules.map((rule) => (
            <MarketingRouterRuleTableRow
              key={`${rule.marketingRouterId}-${rule.id}`}
              rule={rule}
              tenant={tenant}
              today={today}
              isExpanded={expandedRuleId === rule.id}
              onToggleExpanded={() => setExpandedRuleId(expandedRuleId === rule.id ? null : rule.id)}
            />
          ))}
          {rulesQuery.isLoading || (rulesQuery.isSuccess && rules.length === 0) ? (
            <TableRow>
              <TableCell colSpan={MARKETING_ROUTER_RULE_TABLE_COLUMNS}>
                <Typography color="text.secondary">
                  {rulesQuery.isLoading
                    ? 'Loading marketing router tests…'
                    : 'No marketing router test applies this channel markup.'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Stack>
  );
}
